import {
  collectCredentialIssues,
  formatValidationMessages,
  getPasswordRuleStatuses,
  OPERATOR_PASSWORD_POLICY,
  validateEmail,
  validatePassword,
  validateSignupCredentials,
  type PasswordPolicy,
  type ValidationIssue,
} from '@c4ytechnology/auth-validation';

/**
 * Mirrors admin-api `ADMIN_PASSWORD_MIN_LENGTH` (default 12).
 * Set `VITE_ADMIN_PASSWORD_MIN_LENGTH` in admin/.env when the API value changes.
 */
function readMinLength(): number {
  const raw = import.meta.env?.VITE_ADMIN_PASSWORD_MIN_LENGTH;
  if (raw === undefined || raw === '') {
    return OPERATOR_PASSWORD_POLICY.minLength;
  }
  const n = Number.parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : OPERATOR_PASSWORD_POLICY.minLength;
}

export const ADMIN_PASSWORD_MIN_LENGTH = readMinLength();

export { getPasswordRuleStatuses };
export type { PasswordPolicy, ValidationIssue };

export function getAdminPasswordPolicy(): PasswordPolicy {
  return {
    ...OPERATOR_PASSWORD_POLICY,
    minLength: ADMIN_PASSWORD_MIN_LENGTH,
  };
}

export function validateAdminPasswordIssues(password: string): ValidationIssue[] {
  return validatePassword(password, getAdminPasswordPolicy());
}

/** Returns an error message when invalid, or null when acceptable. */
export function validateAdminPassword(password: string): string | null {
  const issues = validateAdminPasswordIssues(password);
  return issues[0]?.message ?? null;
}

/** Login / reset: do not block likely typos (existing operator accounts may use them). */
export function validateAdminEmailIssues(email: string): ValidationIssue[] {
  return validateEmail(email, { blockDisposable: true, blockLikelyTypos: false });
}

export function formatAdminCredentialErrors(issues: ValidationIssue[]): string {
  return formatValidationMessages(issues);
}

/** Password complexity codes owned by the live checklist UI (not the red Alert). */
export const PASSWORD_RULE_ISSUE_CODES = new Set([
  'PASSWORD_REQUIRED',
  'TOO_SHORT',
  'TOO_LONG',
  'MISSING_UPPERCASE',
  'MISSING_LOWERCASE',
  'MISSING_DIGIT',
  'MISSING_SPECIAL',
]);

export function isPasswordRuleIssue(issue: ValidationIssue): boolean {
  return issue.field === 'password' && PASSWORD_RULE_ISSUE_CODES.has(issue.code);
}

/**
 * Issues safe for the auth Alert when a password checklist is visible.
 * Rule failures stay on the checklist; mismatch stays on the confirm-password field.
 */
export function issuesForCredentialAlert(issues: ValidationIssue[]): ValidationIssue[] {
  return issues.filter(
    (issue) => !isPasswordRuleIssue(issue) && issue.code !== 'PASSWORD_MISMATCH',
  );
}

/** Banner copy when only checklist/mismatch failures remain after filtering. */
export function passwordRequirementsAlertMessage(
  issues: ValidationIssue[],
): string | null {
  const hasRules = issues.some(isPasswordRuleIssue);
  const hasMismatch = issues.some((issue) => issue.code === 'PASSWORD_MISMATCH');
  if (!hasRules && !hasMismatch) return null;
  if (hasRules && hasMismatch) {
    return 'Please meet the password requirements and confirm your password.';
  }
  if (hasMismatch) return 'Passwords do not match.';
  return 'Please meet the password requirements below.';
}

export function validateAdminSignupCredentials(input: {
  email: string;
  password: string;
  confirmPassword?: string;
}): ValidationIssue[] {
  return validateSignupCredentials(input, getAdminPasswordPolicy());
}

export function collectAdminCredentialIssues(email: string, password: string): ValidationIssue[] {
  return collectCredentialIssues(email, password, getAdminPasswordPolicy());
}
